import { Box, Button, Stack, TableCell, TableRow, styled, useTheme } from '@mui/material';
import { ExtendedMenuIcon, ShoppingCartIcon } from '@synergy/theme-next';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { MouseEvent, ReactElement, useState } from 'react';
import {
    PoolBanner,
    PoolButton,
    PoolImage,
    PoolLink,
    PoolQuantityPicker,
    PoolQuickActionButton,
    PoolRow,
} from '@synergy/components-next';
import { PoolFormattedMessage } from '@synergy/components-next';
import { PoolMath } from '@synergy/utils';
import { useParams } from 'react-router';

import { ArticlePriceMobile } from './article-price-mobile';
import { Attribute } from '../../../../models/attribute';
import { DeliveryStatus } from '../../../../components/delivery-status/delivery-status';
import { ROUTES } from '../../../../utils/routes';
import { RowArticleMobileMenu } from './row-article-mobile-menu';
import { VerticalDivider } from '../../../../components/vertical-divider/vertical-divider';
import { WishListItem, WishListItemStatus } from '../../../../models/wish-list';
import { WishListMoveItemModal } from '../../modals/wish-list-move-item-modal';
import { WishListType } from '../../../../models/enums/wish-list-type';
import { addArticleToCart } from '../../../../store/shopping-cart-article-actions';
import { getBannerMessage } from '../utlis/get-banner-message';
import { openInquiryModal } from '../../../../store/single-inquiry-actions';
import { removeFromWishlist, setWishListArticleQuantity } from '../../../../store/whish-list-actions';
import { useAppDispatch, useAppSelector } from '../../../../../store/store';
import { wishListDetailSelector } from '../../../../store/whish-list-selectors';

const ProductImage = styled(PoolImage)(() => ({
    display: 'block',
    objectFit: 'contain',
    height: '100px',
    width: '100px',
}));

const QuickActionOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    right: theme.spacing(2.5),
    top: theme.spacing(2),
}));

export interface SupplierTableRowProps {
    cartItem: WishListItem;
    previewImage: string | undefined;
}

export const RowArticleMobile = (props: SupplierTableRowProps): ReactElement => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { cartItem, previewImage } = props;
    const article = cartItem.product;
    const [quantity, setQuantity] = useState(cartItem.quantity);
    const [menuButtonRef, setMenuButtonRef] = useState<null | HTMLElement>(null);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const { id: paramId = '' } = useParams<{ id?: string }>();
    const wishListId = +paramId || 'default';
    const wishList = useAppSelector(wishListDetailSelector);

    const canEdit = wishListId === 'default' || wishList?.isOwner || wishList?.type === WishListType.OrganizationEdit;

    const handleShowMenu = (event: MouseEvent<HTMLElement>): void => {
        setMenuButtonRef(event.currentTarget);
    };

    const handleDeleteArticle = (): void => {
        dispatch(removeFromWishlist(wishListId, cartItem.id));
    };

    const handleMoveArticle = (): void => {
        setShowMoveModal(true);
    };

    const handleQuantityChange = (quantity: number): void => {
        dispatch(setWishListArticleQuantity(wishListId, cartItem.id, quantity));
        setQuantity(quantity);
    };

    const handleAddToShoppingCart = (): void => {
        dispatch(addArticleToCart(cartItem.product.id, quantity));
    };

    const handleCreateSingleInquiry = (): void => {
        dispatch(
            openInquiryModal({
                items: [{ id: article.id, articleName: article.name, quantity: article.minimumOrderQuantity }],
            })
        );
    };

    return (
        <>
            {cartItem.status !== WishListItemStatus.Available && (
                <TableRow>
                    <TableCell sx={{ px: 0, pb: 0, pt: 2, borderBottom: 'none' }}>
                        <PoolBanner severity={cartItem.status === WishListItemStatus.Removed ? 'error' : 'warning'}>
                            <FormattedMessage id={getBannerMessage(cartItem.status)} />
                        </PoolBanner>
                    </TableCell>
                </TableRow>
            )}

            <TableRow>
                <TableCell sx={{ position: 'relative' }}>
                    <PoolRow alignItems="flex-start" gap={2} sx={{ py: 1 }}>
                        <Stack justifyContent="flex-start">
                            <PoolLink href={ROUTES.article.detail.replace(':id', article.id.toString())}>
                                <ProductImage
                                    src={previewImage}
                                    alt={article.name}
                                    sx={{ opacity: cartItem.status === WishListItemStatus.Removed ? 0.5 : 1 }}
                                />
                            </PoolLink>
                        </Stack>

                        <PoolRow gap={0} alignItems="top" sx={{ width: '100%' }}>
                            <Stack gap={1.5} typography="body2" flex={1}>
                                <Stack
                                    sx={{ opacity: cartItem.status === WishListItemStatus.Removed ? 0.5 : 1 }}
                                    color="grey.800"
                                >
                                    <PoolLink
                                        colorVariant="black"
                                        href={ROUTES.article.detail.replace(':id', article.id.toString())}
                                        typography="subtitle1"
                                        sx={{ pr: 6 }}
                                    >
                                        {article.name}
                                    </PoolLink>
                                    <Box sx={{ mt: 1 }}>
                                        <PoolFormattedMessage
                                            id={'app.cart.article.articleNumber'}
                                            values={{ articleNumber: article.number }}
                                        />
                                    </Box>
                                    {article.globalTradeItemNumber && (
                                        <Box>
                                            <PoolFormattedMessage
                                                id={'app.cart.article.globalNumber'}
                                                values={{ articleNumber: article.globalTradeItemNumber }}
                                            />
                                        </Box>
                                    )}

                                    {article.attributes && (
                                        <PoolRow divider={<VerticalDivider />} gap={1} flexWrap="wrap" rowGap={0}>
                                            {article.attributes.map((attribute: Attribute) => (
                                                <Box key={attribute.id}>
                                                    {attribute.name} {attribute.value}
                                                </Box>
                                            ))}
                                        </PoolRow>
                                    )}
                                </Stack>

                                {cartItem.status === WishListItemStatus.Available && (
                                    <Stack alignItems="flex-start">
                                        <Box
                                            color={
                                                article.specialPrice
                                                    ? theme.palette.error.dark
                                                    : theme.palette.common.black
                                            }
                                            typography="h5"
                                            fontWeight={600}
                                        >
                                            <FormattedNumber
                                                value={PoolMath.round(cartItem.netPrice * quantity)}
                                                currency={article.currency}
                                                style="currency"
                                            />
                                        </Box>
                                        <ArticlePriceMobile article={article} netPrice={cartItem.netPrice} />
                                    </Stack>
                                )}

                                <Stack gap={0.5}>
                                    <DeliveryStatus
                                        variant="circle"
                                        deliveryStatus={article.deliveryStatus}
                                        stockStatus={article.stockStatus}
                                        deliveryTimeInDays={article.deliveryTimeInDays}
                                    />
                                    {cartItem.status === WishListItemStatus.Available && (
                                        <>
                                            <PoolRow gap={2}>
                                                <PoolQuantityPicker
                                                    minQuantity={article.minimumOrderQuantity}
                                                    maxQuantity={article.maximumOrderQuantity}
                                                    interval={article.quantityInterval}
                                                    onQuantityChange={handleQuantityChange}
                                                    disabled={!canEdit}
                                                />
                                                <PoolButton
                                                    sx={{ mr: 1 }}
                                                    startIcon={<ShoppingCartIcon />}
                                                    onClick={handleAddToShoppingCart}
                                                />
                                            </PoolRow>
                                            <Box color="grey.800" typography="body2">
                                                <Box>
                                                    <FormattedMessage
                                                        id="app.cart.article.quantity"
                                                        values={{
                                                            orderUnit: article.orderUnit,
                                                            contentQuantity: article.contentQuantity,
                                                            contentUnit: article.contentUnit,
                                                            optional: chunks =>
                                                                article.contentQuantity &&
                                                                article.contentUnit &&
                                                                chunks,
                                                        }}
                                                    />
                                                </Box>
                                                {(article.minimumOrderQuantity ||
                                                    (article.quantityInterval && article.quantityInterval !== 1)) && (
                                                    <Stack direction="row">
                                                        {'('}
                                                        <Stack
                                                            direction="row"
                                                            typography="body2"
                                                            color="grey.800"
                                                            gap={1}
                                                            divider={<VerticalDivider />}
                                                        >
                                                            {article.minimumOrderQuantity && (
                                                                <Box>
                                                                    <PoolFormattedMessage
                                                                        id={'app.cart.article.minimumQuantity'}
                                                                        values={{
                                                                            minimumQuantity:
                                                                                article.minimumOrderQuantity,
                                                                        }}
                                                                    />
                                                                </Box>
                                                            )}
                                                            {article.quantityInterval &&
                                                                article.quantityInterval !== 1 && (
                                                                    <PoolFormattedMessage
                                                                        id={'app.cart.article.interval'}
                                                                        values={{
                                                                            interval: article.quantityInterval,
                                                                        }}
                                                                    />
                                                                )}
                                                        </Stack>
                                                        {')'}
                                                    </Stack>
                                                )}
                                            </Box>
                                        </>
                                    )}
                                </Stack>
                            </Stack>
                            {canEdit && (
                                <QuickActionOverlay>
                                    <PoolQuickActionButton
                                        onClick={handleShowMenu}
                                        color={cartItem.status === WishListItemStatus.Removed ? 'error' : 'medium'} //medium
                                    >
                                        <ExtendedMenuIcon />
                                    </PoolQuickActionButton>
                                    <RowArticleMobileMenu
                                        open={!!menuButtonRef}
                                        anchorEl={menuButtonRef}
                                        isAvailableForPurchase={cartItem.status !== WishListItemStatus.Removed}
                                        onClose={() => setMenuButtonRef(null)}
                                        onMove={handleMoveArticle}
                                        onDelete={handleDeleteArticle}
                                    />
                                </QuickActionOverlay>
                            )}
                        </PoolRow>
                    </PoolRow>
                    {cartItem.status !== WishListItemStatus.Available && (
                        <Button variant="outlined" sx={{ my: 1 }} fullWidth={true} onClick={handleCreateSingleInquiry}>
                            <FormattedMessage id="app.singleInquiry" />
                        </Button>
                    )}
                </TableCell>
            </TableRow>

            <WishListMoveItemModal
                open={showMoveModal}
                wishListItem={cartItem}
                onClose={() => {
                    setShowMoveModal(false);
                    setMenuButtonRef(null);
                }}
            />
        </>
    );
};
